import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Loader2, KeyRound, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import SystemSettingsCard from "../components/SystemSettingsCard";

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2.5 mb-4">
        <KeyRound className="w-5 h-5 text-[#c15337]" />
        <h2 className="font-heading text-lg font-bold text-[#114232]">Change password</h2>
      </div>
      <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="change-password-form">
        <div>
          <Label htmlFor="cp-current">Current password</Label>
          <Input id="cp-current" type="password" required minLength={1}
                  data-testid="change-password-current"
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="cp-new">New password (min 8 chars)</Label>
          <Input id="cp-new" type="password" required minLength={8}
                  data-testid="change-password-new"
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="cp-confirm">Confirm new password</Label>
          <Input id="cp-confirm" type="password" required minLength={8}
                  data-testid="change-password-confirm"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        </div>
        <Button type="submit" disabled={saving} data-testid="change-password-submit">
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Update password
        </Button>
      </form>
    </section>
  );
}

function TwoFactorCard() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  async function reload() {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => { reload(); }, []);

  async function startSetup() {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifySetup(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null);
      setCode("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function disable2fa(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA disabled");
      setDisableCode("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="two-factor-card">
      <div className="flex items-center gap-2.5 mb-4">
        {status?.totp_enabled ? <ShieldCheck className="w-5 h-5 text-[#114232]" /> : <Smartphone className="w-5 h-5 text-[#c15337]" />}
        <h2 className="font-heading text-lg font-bold text-[#114232]">Two-factor authentication (TOTP)</h2>
      </div>
      {!status && <Loader2 className="w-5 h-5 animate-spin text-[#114232]" />}

      {status && !status.totp_enabled && !setup && (
        <div className="space-y-3">
          <p className="text-sm text-[#5c6360]">
            Add an extra layer of protection by requiring a 6-digit code from your authenticator app at each sign-in.
            Compatible with Google Authenticator, 1Password, Authy, etc.
          </p>
          <Button onClick={startSetup} disabled={busy} data-testid="2fa-start-setup"
                    className="bg-[#c15337] hover:bg-[#a3432a]">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Enable 2FA
          </Button>
        </div>
      )}

      {setup && (
        <form onSubmit={verifySetup} className="space-y-4" data-testid="2fa-setup-form">
          <div className="flex flex-wrap gap-6 items-start">
            <img
              src={`data:image/png;base64,${setup.qr_code_png_base64}`}
              alt="2FA QR code"
              className="w-40 h-40 border border-[#114232]/15 rounded p-1 bg-white"
              data-testid="2fa-qr"
            />
            <div className="flex-1 min-w-[220px] space-y-2 text-sm">
              <p className="text-[#5c6360]">1. Scan the QR with your authenticator app.</p>
              <p className="text-[#5c6360]">2. Or paste this key manually:</p>
              <code className="block bg-[#114232]/5 border border-[#114232]/15 rounded px-2 py-1 font-mono text-xs break-all"
                    data-testid="2fa-secret">{setup.secret}</code>
              <p className="text-[#5c6360]">3. Enter the 6-digit code below to confirm.</p>
            </div>
          </div>
          <div className="max-w-xs">
            <Label htmlFor="2fa-code">Verification code</Label>
            <Input id="2fa-code" inputMode="numeric" pattern="[0-9]{6,8}" maxLength={8}
                    required value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="2fa-verify-input"
                    className="font-mono tracking-[0.3em] text-center" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || code.length < 6} data-testid="2fa-verify-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Confirm & enable
            </Button>
            <Button type="button" variant="outline" onClick={() => { setSetup(null); setCode(""); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {status && status.totp_enabled && (
        <form onSubmit={disable2fa} className="space-y-3" data-testid="2fa-disable-form">
          <div className="bg-[#114232]/6 border border-[#114232]/15 rounded p-3 text-sm text-[#114232] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            <span>2FA is active on your account.</span>
          </div>
          <Label className="block">
            To disable, enter a current 6-digit code from your app:
            <Input className="mt-1.5 max-w-xs font-mono tracking-[0.3em] text-center"
                    inputMode="numeric" pattern="[0-9]{6,8}" maxLength={8}
                    required value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="2fa-disable-input" />
          </Label>
          <Button type="submit" variant="outline" disabled={busy || disableCode.length < 6}
                    data-testid="2fa-disable-submit"
                    className="text-[#c15337] border-[#c15337]/30 hover:bg-[#c15337]/10">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-1.5" />}
            Disable 2FA
          </Button>
        </form>
      )}
    </section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <Toaster position="top-right" />
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#114232]">Account settings</h1>
        <p className="text-xs text-[#5c6360] mt-1">
          Signed in as {user?.email}
        </p>
      </div>
      <ChangePasswordCard />
      <TwoFactorCard />
      {user?.role === "super_admin" && <SystemSettingsCard />}
    </div>
  );
}
